import { QrcodeOutlined, PictureOutlined } from '@ant-design/icons'
import { type WhatsappTemplate } from '../types'
import { type MergeTagOptions } from '@/lib/mergeTags'
import {
  type HeaderImageType,
  labelForMergeTagValue,
  headerTextHasPlaceholder,
  renderHeaderTextPreview,
} from './helpers'

interface WhatsAppPreviewProps {
  template: WhatsappTemplate
  previewText: string
  headerImageType: HeaderImageType
  headerImageUrl: string
  headerTextValue: string
  mergeTagOptions: MergeTagOptions
  parameterValues: Record<string, string>
  allSlots: number[]
}

const WhatsAppPreview = ({
  template,
  previewText,
  headerImageType,
  headerImageUrl,
  headerTextValue,
  mergeTagOptions,
  parameterValues,
  allSlots,
}: WhatsAppPreviewProps) => {
  const hasMappings = allSlots.length > 0 || template.hasHeaderImage || template.hasHeaderText

  return (
    <div className="sticky top-6">
      <div className={`flex ${hasMappings ? 'gap-4' : ''}`}>
        {/* WhatsApp chat bubble */}
        <div className="flex-1 min-w-0">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Message Preview</span>
          </div>

          <div className="rounded-xl bg-[#e5ddd5] p-4">
            <div className="mb-3 flex items-center gap-2">
              <div className="flex size-8 items-center justify-center rounded-full bg-[#128C7E] text-[10px] font-bold text-white">
                WA
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-700">AVA Events</div>
                <div className="text-[10px] text-gray-500">Campaign message</div>
              </div>
            </div>

            <div className="max-w-[360px] overflow-hidden rounded-lg rounded-tl-none bg-white shadow-sm">
              {/* Header image */}
              {template.hasHeaderImage && (
                <div className="flex h-36 w-full items-center justify-center bg-gray-100">
                  {headerImageType === 'attendee.qr_code' ? (
                    <div className="flex flex-col items-center gap-1 text-gray-400">
                      <QrcodeOutlined style={{ fontSize: 36 }} />
                      <span className="text-[10px]">QR Code</span>
                    </div>
                  ) : headerImageUrl.trim() ? (
                    <img
                      src={headerImageUrl.trim()}
                      alt="Header"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-1 text-gray-300">
                      <PictureOutlined style={{ fontSize: 36 }} />
                      <span className="text-[10px]">No image</span>
                    </div>
                  )}
                </div>
              )}

              {/* Header text */}
              {template.hasHeaderText && template.headerText && (
                <div className="border-b border-gray-100 px-3 pt-3 pb-2">
                  <div className="text-[14px] font-semibold leading-snug text-gray-900">
                    {renderHeaderTextPreview(
                      template.headerText,
                      headerTextValue || undefined,
                      mergeTagOptions,
                    )}
                  </div>
                </div>
              )}

              {/* Body */}
              <div className="p-3">
                <div className="whitespace-pre-wrap text-[13px] leading-relaxed text-gray-800">
                  {previewText || 'No content available for this template.'}
                </div>
                <div className="mt-2 text-right text-[10px] text-gray-400">
                  {new Date().toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mapping summary — side by side */}
        {hasMappings && (
          <div className="w-56 shrink-0">
            <div className="mb-3 flex items-center">
              <span className="text-sm font-medium text-gray-700">Mapping Summary</span>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white px-3 py-3">
              <div className="space-y-2">
                {template.hasHeaderImage && (
                  <div className="text-[11px]">
                    <div className="font-mono text-gray-500">header_image</div>
                    <div className="mt-0.5 text-gray-600">
                      {headerImageType === 'attendee.qr_code'
                        ? 'Attendee QR Code'
                        : headerImageUrl.trim() || '\u2014'}
                    </div>
                  </div>
                )}
                {template.hasHeaderText &&
                  template.headerText &&
                  headerTextHasPlaceholder(template) && (
                    <div className="text-[11px]">
                      <div className="font-mono text-gray-500">header_text</div>
                      <div
                        className={`mt-0.5 ${headerTextValue ? 'text-gray-600' : 'italic text-gray-300'}`}
                      >
                        {headerTextValue
                          ? labelForMergeTagValue(headerTextValue, mergeTagOptions)
                          : 'not mapped'}
                      </div>
                    </div>
                  )}
                {allSlots.map((slot) => {
                  const val = parameterValues[String(slot)]
                  return (
                    <div key={slot} className="text-[11px]">
                      <div className="font-mono text-gray-500">{`{{${slot}}}`}</div>
                      <div
                        className={`mt-0.5 ${val ? 'text-gray-600' : 'italic text-gray-300'}`}
                      >
                        {val
                          ? labelForMergeTagValue(val, mergeTagOptions)
                          : 'not mapped'}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default WhatsAppPreview
